import {
  currencySelector,
  hostnameSelector,
  langSelector,
  userSelector,
} from './../App/selectors';
import { AppThunk, RootState } from './../../rootReducer';
import { createSlice, PayloadAction, createSelector } from '@reduxjs/toolkit';
import APIClass, { ThenArg } from '../../services/API';
import { prop } from '../../utilities';
import { resolveDomainByHostname } from '../../configureTrans';
import { cartIdSelector } from '../Cart/cartSlice';
import * as cookie from 'react-cookies';
import { __ } from 'react-i18n';
import { getAttribParameterFromAttribObj } from '../../utilities/category';
import { ComparisonAttribsDataItem } from 'eshop-defaults';

interface ComparisonState {
  products: any[] | null;
  sharedProducts: any[] | null;
  attribsData: ComparisonAttribsDataItem[];
  isFetching: boolean;
}

export const initialState: ComparisonState = {
  products: null,
  sharedProducts: null,
  attribsData: [],
  isFetching: false,
};

const comparisonSlice = createSlice({
  name: 'comparison',
  initialState,
  reducers: {
    setProducts(state, action: PayloadAction<{ products: any[] }>) {
      state.products = action.payload.products;
    },
    setSharedProducts(state, action: PayloadAction<{ sharedProducts: any[] }>) {
      state.sharedProducts = action.payload.sharedProducts;
    },
    setIsFetching(state, action: PayloadAction<{ isFetching: boolean }>) {
      state.isFetching = action.payload.isFetching;
    },
    setAttribsData(
      state,
      action: PayloadAction<{ attribsData: ComparisonAttribsDataItem[] }>,
    ) {
      state.attribsData = action.payload.attribsData;
    },
  },
});

const {
  setProducts,
  setSharedProducts,
  setIsFetching,
  setAttribsData,
} = comparisonSlice.actions;
export default comparisonSlice.reducer;

const comparisonDomainSelector = (state: RootState) => state.comparison;

export const comparisonProductsSelector = createSelector(
  comparisonDomainSelector,
  substate => prop(substate, 'products', null),
);

export const loadcomparisonProductsDataFromProductPlus = (
  productPlus: string[],
  shareMode: boolean,
) => {
  return async (dispatch, getState, API) => {
    let productIds = [];

    if (productPlus?.filter(o => o).length) {
      const { products } = await API.searchProducts({
        plu: productPlus.join(','),
      });
      productIds = products.map(product => product.product_id);
    }

    await dispatch(loadcomparisonProductsData(productIds, shareMode));
  };
};

export const loadcomparisonProductsData = (
  productIds: number[],
  shareMode: boolean,
) => {
  return async (dispatch, getState, API) => {
    if (!productIds.length) {
      dispatch(setProducts({ products: [] }));
      dispatch(setAttribsData({ attribsData: [] }));
      return;
    }

    dispatch(setIsFetching({ isFetching: true }));

    const lang: string = langSelector(getState());
    const cartId = cartIdSelector(getState());
    const hostname = hostnameSelector(getState());
    const user = userSelector(getState());

    const isB2B = user && user.b2b;

    const elasticCategoryPromise = API.loadElasticCategoryProducts(-1, {
      domainId: resolveDomainByHostname(hostname),
      langId: lang,
      cartId,
      onlySubcategories: 0,
      productIds,
      limit: productIds.length,
      withPublish: '1',
    });

    const productAttribGroupsPromise = API.loadProductAttribsGroups({
      sort: 'group_sort',
      sortDir: 'desc',
    });

    const [elasticCategory, productAttribGroups] = await Promise.all([
      elasticCategoryPromise,
      productAttribGroupsPromise,
    ]);

    const products = elasticCategory.products.products;
    const categoryAttribs = elasticCategory.attribsAggs;
    const attribGroups = productAttribGroups.productAttribGroups;

    const productsAttribs: ComparisonAttribsDataItem[] = [];

    for (const product of products) {
      const productDetail = product.product_detail;
      const attribs = productDetail.attribs;
      for (const attrib of attribs) {
        const attribGroup = attribGroups.find(
          attribGroup => attribGroup.group_id === attrib.group_id,
        );
        let currentProductAttrib = productsAttribs.find(
          productsAttrib => productsAttrib.groupId === attribGroup?.group_id,
        );
        if (attribGroup && !currentProductAttrib) {
          currentProductAttrib = {
            groupName: getAttribParameterFromAttribObj(
              attribGroup,
              'group_name',
              lang,
            ),
            groupId: attribGroup.group_id,
            groupSort: attribGroup.group_sort,
            attribs: [],
          };
          productsAttribs.push(currentProductAttrib);
        } else if (!currentProductAttrib) {
          currentProductAttrib = productsAttribs.find(
            productsAttrib => productsAttrib.groupId === 0,
          );
          if (!currentProductAttrib) {
            currentProductAttrib = {
              groupName: __('Ostatné parametre'),
              groupId: 0,
              groupSort: 9999,
              attribs: [],
            };
            productsAttribs.push(currentProductAttrib);
          }
        }

        if (currentProductAttrib) {
          let currentAttrib = currentProductAttrib.attribs.find(
            cpAttrib => cpAttrib.attribId === attrib.attrib_id,
          );
          if (!currentAttrib) {
            currentAttrib = {
              attribId: attrib.attrib_id,
              attribName: getAttribParameterFromAttribObj(
                attrib,
                'attrib_name',
                lang,
              ),
              attribShortDescription: getAttribParameterFromAttribObj(
                attrib,
                'attrib_short_description',
                lang,
              ),
              attribType: attrib.attrib_type,
              valueBestSort: attrib.value_best_sort,
              attribUnit: attrib.attrib_unit,
              attribSort: attrib.attrib_sort,
              typeId: attrib.type_id,
              groupId: attrib.group_id,
              values: [],
            };
            currentProductAttrib.attribs.push(currentAttrib);
          }

          currentAttrib.values.push({
            productId: product.product_id,
            highlight: false,
            values: attrib.values.map(value => {
              const categoryAttribValue = categoryAttribs[
                value.attrib_id
              ]?.values?.find(
                categoryAttribvalue =>
                  categoryAttribvalue?.info?.value_id === value.value_id,
              );
              return {
                name: getAttribParameterFromAttribObj(
                  value,
                  'attrib_value',
                  lang,
                ),
                link: getAttribParameterFromAttribObj(
                  categoryAttribValue?.info,
                  'attrib_link',
                  lang,
                ),
                shortDescription: getAttribParameterFromAttribObj(
                  categoryAttribValue?.info,
                  'attrib_short_description',
                  lang,
                ),
                numberRating: getAttribParameterFromAttribObj(
                  categoryAttribValue?.info,
                  'number_rating',
                  lang,
                ),
              };
            }),
          });
        }
      }
    }

    productsAttribs.sort((a, b) => a.groupSort - b.groupSort);
    for (const productsAttrib of productsAttribs) {
      productsAttrib.attribs.sort((a, b) => {
        if (a.typeId !== b.typeId) {
          return a.typeId - b.typeId;
        }

        if (a.attribSort !== b.attribSort) {
          return a.attribSort - b.attribSort;
        }

        return a.attribId - b.attribId;
      });
    }

    // highlights
    /*
      for (const productAttrib of productsAttribs) {
        for (const attrib of productAttrib.attribs) {
          if (attrib.values.length >= 2) {
            const valueMap = new Map<string, number>();
            for (const value of attrib.values) {
              const key = value.values.map(v => `${v.name}`).join('||'); // separator between items
              valueMap.set(key, (valueMap.get(key) ?? 0) + 1);
            }
  
            attrib.values = attrib.values.map(value => {
              const key = value.values.map(v => `${v.name}`).join('||');
              return {
                ...value,
                highlight: valueMap.get(key) === 1,
              };
            });
          }
        }
      }
      */

    // positive highlights
    try {
      for (const productAttrib of productsAttribs) {
        for (const attrib of productAttrib.attribs) {
          if (attrib.values.length >= 2) {
            if (
              attrib.attribType === 'rozsah' &&
              [-1, 1].includes(attrib.valueBestSort)
            ) {
              const highlightOption = attrib.values
                .map(value => parseFloat(value.values[0].name))
                .sort((a, b) => {
                  return attrib.valueBestSort === -1 ? a - b : b - a;
                })[0];

              let numberOfHighlights = 0;
              for (const value of attrib.values) {
                if (
                  value.values
                    .map(value => parseFloat(value.name))
                    .includes(highlightOption)
                ) {
                  numberOfHighlights++;
                }
              }

              if (numberOfHighlights < attrib.values.length) {
                for (const value of attrib.values) {
                  if (
                    value.values
                      .map(value => parseFloat(value.name))
                      .includes(highlightOption)
                  ) {
                    value.highlight = true;
                  }
                }
              }
            } else if (attrib.attribType === 'zakladny') {
              const hasAllOneValue =
                attrib.values.length ===
                attrib.values.filter(value => value.values.length === 1).length;
              const numberRatings = attrib.values.map(
                value => value.values[0].numberRating,
              );
              const uniqueRatings = numberRatings.filter(
                (item, i, ar) => ar.indexOf(item) === i,
              );

              if (hasAllOneValue && uniqueRatings.length > 1) {
                const bestValue = attrib.values.sort(
                  (a, b) => a.values[0].numberRating - b.values[0].numberRating,
                )[0].values[0].name;

                for (const value of attrib.values) {
                  if (
                    value.values.map(value => value.name).includes(bestValue)
                  ) {
                    value.highlight = true;
                  }
                }
              }
            }
          }
        }
      }
    } catch (e) {}

    if (shareMode) {
      dispatch(setSharedProducts({ sharedProducts: products }));
    } else {
      dispatch(setProducts({ products: products }));
    }
    dispatch(setAttribsData({ attribsData: productsAttribs }));
    dispatch(setIsFetching({ isFetching: false }));
  };
};

export const resetProductsData = () => {
  return async (dispatch, getState, API) => {
    dispatch(setProducts({ products: [] }));
    dispatch(setAttribsData({ attribsData: [] }));
  };
};
